import { ValidationMessageKeys } from 'src/Configuration/validation'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DownloadFileItem, DownloadsParent, UpdateFileFormValues } from '../models'
import { validateUrlName } from '../api/downloads.api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { z } from 'zod'
import { THIRTY_MB } from '../consts'

const MIN_STRING_LENGTH = 3
const MAX_STRING_LENGTH = 60
const ALLOWED_FILE_NAME_REGEX = /^(?!^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(\.|$))[^\\/:*?"<>|\r\n]+(?<![ .])$/i

interface Props {
  item?: DownloadFileItem
  parent?: DownloadsParent
  isPublic?: boolean
  isUpload?: boolean
}

/**
 *  Initializes the form for uploading a file
 * @param item DownloadsDto
 * @returns initialValues and validationSchema for fileUpload form
 */
export const useUploadFileFormInitializer = ({
  item,
  parent,
  isPublic,
  isUpload = false,
}: Props): { initialValues: UpdateFileFormValues; validationSchema: z.Schema } => {
  const { t, selectedAppLanguage } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const fileSizeLimit = THIRTY_MB

  const initialValues: UpdateFileFormValues = {
    parentId: parent?.id,
    fileName: item?.fileName ?? '',
    language: item?.language ?? (selectedAppLanguage as UpdateFileFormValues['language']),
    urlName: item?.urlName ?? '',
    isPublic: !!isPublic,
    countryList: item?.countryList ?? [],
    visibleToCustomer: item?.visibleToCustomer ?? false,
    visibleToInstaller: item?.visibleToInstaller ?? false,
  }

  const filePropsValidationSchema = z
    .object({
      fileName: z
        .string()
        .min(MIN_STRING_LENGTH, t(ValidationMessageKeys.lengthMin, { value: MIN_STRING_LENGTH }))
        .max(MAX_STRING_LENGTH, t(ValidationMessageKeys.lengthMax, { value: MAX_STRING_LENGTH }))
        .trim()
        .refine(value => ALLOWED_FILE_NAME_REGEX.test(value), {
          message: t('documents.upload.not-allowed-file-name'),
        }),
      urlName: z.string().trim().optional(),
      language: z.string(),
      countryList: z.array(z.string()).min(1, t(ValidationMessageKeys.required)),
      visibleToCustomer: z.boolean(),
      visibleToInstaller: z.boolean(),
      isPublic: z.boolean(),
    })
    .superRefine(async (data, ctx) => {
      if (data.isPublic === false) {
        return
      }

      if (!data.urlName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['urlName'],
          message: t(ValidationMessageKeys.required),
        })
        return
      }

      if (data.urlName === item?.urlName) {
        return
      }

      if (data.isPublic && data.urlName.length < MIN_STRING_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['urlName'],
          message: t(ValidationMessageKeys.lengthMin, { value: MIN_STRING_LENGTH }),
        })
        return
      }

      if (data.urlName.length > MAX_STRING_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['urlName'],
          message: t(ValidationMessageKeys.lengthMax, { value: MAX_STRING_LENGTH }),
        })
        return
      }

      const result = await validateUrlName(data.urlName, authFetch)
      if (result.hasErrors || result.value !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['urlName'],
          message: t('validation.url-name.already-exists'),
        })
      }
    })

  const uploadValidationSchema = z.object({
    file: z
      .instanceof(File, { message: t(ValidationMessageKeys.required) })
      .refine(file => file.size <= fileSizeLimit, {
        message: t('documents.upload.file-size-error'),
      })
      .refine(
        file => {
          const name = file.name.split('.')
          return name[0].length > 3
        },
        {
          message: t('documents.upload.short-name-error'),
        }
      )
      .refine(file => file.name.length < 100, {
        message: t('documents.upload.long-name-error'),
      }),
  })

  const validationSchema = isUpload ? filePropsValidationSchema.and(uploadValidationSchema) : filePropsValidationSchema

  return { initialValues, validationSchema }
}
